import ResourceModule, { type ResourceModuleConfig } from "@/pages/admin/components/ResourceModule";
import { StatusPill, type Column } from "@/pages/admin/components/AdminUI";
import { optionsFrom } from "@/lib/adminContent";
import { AUDIT_ACTION, metaFor, type AuditLog } from "@/lib/adminSystem";
import { formatDate } from "@/pages/admin/format";

const columns: Column<AuditLog>[] = [
  {
    key: "action",
    header: "Action",
    primary: true,
    render: (row) => (
      <div className="min-w-0 max-w-md">
        <div className="flex items-center gap-2">
          <StatusPill {...metaFor(AUDIT_ACTION, row.action)} />
          <span className="text-xs text-foreground-500 whitespace-nowrap">{row.entity}</span>
        </div>
        <p className="text-sm text-foreground-800 mt-1.5 line-clamp-1">{row.detail || "—"}</p>
      </div>
    ),
  },
  { key: "actor", header: "By", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.actor || "System"}</span> },
  { key: "entityId", header: "Record", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.entity_id || "—"}</span> },
  { key: "ip", header: "IP", render: (row) => <span className="text-xs text-foreground-500 whitespace-nowrap">{row.ip || "—"}</span> },
  { key: "when", header: "When", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.created_at)}</span> },
];

const config: ResourceModuleConfig<AuditLog> = {
  table: "audit_logs",
  orderBy: "created_at",
  eyebrow: "Administration",
  title: "Audit Logs",
  subtitle: "A tamper-evident trail of every change made in the portal — who did what, to which record, and when.",
  searchPlaceholder: "Search by actor, entity, detail…",
  searchKeys: ["actor", "action", "entity", "entity_id", "detail", "ip"],
  filters: [{ key: "action", label: "Action", options: optionsFrom(AUDIT_ACTION) }],
  readOnly: true,
  stats: (rows) => [
    { label: "Events", value: rows.length, icon: "ri-history-line", tone: "primary" },
    { label: "Creates", value: rows.filter((row) => row.action === "created").length, icon: "ri-add-circle-line", tone: "secondary" },
    { label: "Updates", value: rows.filter((row) => row.action === "updated").length, icon: "ri-edit-line", tone: "secondary" },
    { label: "Deletes", value: rows.filter((row) => row.action === "deleted").length, icon: "ri-delete-bin-line", tone: "accent" },
  ],
  columns,
  fields: [
    { key: "action", label: "Action" },
    { key: "actor", label: "Actor" },
    { key: "entity", label: "Entity" },
    { key: "entity_id", label: "Record ID" },
    { key: "detail", label: "Detail" },
    { key: "ip", label: "IP address" },
    { key: "created_at", label: "When", type: "date" },
  ],
  createLabel: "",
  emptyTitle: "No audit events yet",
  emptyMessage: "Administrative actions are recorded here automatically.",
  newDefaults: {},
  rowTitle: (row) => `${row.action} · ${row.entity || "record"}`,
};

export default function AdminAuditLogs() {
  return <ResourceModule config={config} />;
}